/**
 * /trust — the trust and security centre: GDPR posture, hosting and data
 * residency, the DPA and subprocessors, encryption and access control, sign-in,
 * backup and retention, and how Ava handles employee data.
 *
 * Every word the page renders comes from this file, and so does the route's
 * JSON-LD (`getSchemaForTrust`), so the FAQPage markup cannot restate an answer
 * the visitor does not see. The route is wired like every other public page:
 * `/trust` in `MarketingRoutes.tsx` and `MarketingRoutes.server.tsx`,
 * `TRUST_PATH` in `prerenderRoutes` (with `TRUST_LAST_MODIFIED` in
 * `routeLastModified`), and SEO resolution in `seo/siteSeo.ts`.
 *
 * WHAT THIS PAGE MAY CLAIM. Only what the rest of the site already states:
 * Google Cloud Platform hosting with global data centres and continuous threat
 * monitoring through Security Command Center Premium; TLS on all data in transit,
 * internal services included; signed file links that expire after 24 hours;
 * least-privilege service permissions; role-based access with ownership checks;
 * audit logging of sign-ins, exports, consent records and rating changes; an A+
 * Mozilla Observatory rating; DSAR exports, right-to-be-forgotten workflows and
 * a DPA; export on every plan; biometric sign-in in the mobile apps; and Ava's
 * posture from the AI page (labelled, editable, human-approved output, isolated
 * tenants, no use of one customer's data to train models for another, switchable
 * per cycle, team or module). It states plainly that there is no SAML SSO or SCIM
 * today, matching /integrations and the Ava prompt in `frontend/api/ava-chat.ts`.
 *
 * It names no certification and no audit report, commits to no hosting region
 * (the repository records none — see the Ireland and Europe page and its test),
 * and publishes no backup schedule or retention period. Each of those is a fact
 * the company has to confirm first; they are listed in `TRUST_FACTS_TO_VERIFY`
 * and marked TODO(verify) beside the copy they would change.
 */
import { SCHEMA_CONTEXT, type JsonLd } from '../seo/structuredData';
import { breadcrumbListSchema, type BreadcrumbItem } from '../seo/breadcrumbs';

// Restated rather than imported from `seo/siteSeo.ts`, which imports this file.
const SITE_ORIGIN = 'https://www.clear-talent.com';
const ORG_ID = `${SITE_ORIGIN}/#organization`;
const WEBSITE_ID = `${SITE_ORIGIN}/#website`;
const SOFTWARE_ID = `${SITE_ORIGIN}/#software`;

export const TRUST_PATH = '/trust';

/**
 * The sitemap `<lastmod>`, the WebPage `dateModified` and the visible "Last
 * reviewed" line. Move it forward when the visible content changes — in
 * particular when an item in `TRUST_FACTS_TO_VERIFY` is confirmed and the copy
 * updated — and not otherwise.
 */
export const TRUST_LAST_MODIFIED = '2026-10-04';
export const TRUST_LAST_MODIFIED_LABEL = '4 October 2026';

/**
 * TODO(verify): facts the company must confirm before this page can state them.
 * Until each is confirmed the page either says nothing or asks the buyer to get
 * the answer in writing. When one is confirmed, update the copy that names its
 * `id` in a TODO(verify) comment below, then move `TRUST_LAST_MODIFIED`.
 */
export const TRUST_FACTS_TO_VERIFY = [
  {
    id: 'certifications',
    question: 'Does CLEAR Talent hold SOC 2, ISO/IEC 27001 or any other certification or third-party audit report?',
  },
  {
    id: 'hosting-region',
    question: 'Which Google Cloud region(s) hold customer data by default, and can an EU-only region be guaranteed contractually?',
  },
  {
    id: 'dpa-download',
    question: 'Is there a standard DPA that can be published as a download (and at what URL), or is it issued per customer?',
  },
  {
    id: 'subprocessors',
    question: 'What is the complete, current subprocessor list — including the AI model provider behind Ava — with purpose and location for each?',
  },
  {
    id: 'encryption-at-rest',
    question: 'How is data encrypted at rest (provider default or customer-managed keys), and may the page say so?',
  },
  {
    id: 'backup-retention',
    question: 'Backup frequency, backup retention period, recovery objectives, and how long data is kept after a contract ends?',
  },
  {
    id: 'ai-training',
    question:
      'Can the page go further than "never used to train models for other customers" and state that customer data is not used to train any model, including by the model provider?',
  },
  {
    id: 'pen-testing',
    question: 'Is there independent penetration testing, and on what cadence, that the page may mention?',
  },
] as const;

export interface TrustItem {
  title: string;
  body: string;
}

export interface Subprocessor {
  name: string;
  purpose: string;
  location: string;
}

export interface TrustSection {
  id: 'gdpr' | 'residency' | 'dpa' | 'security' | 'sign-in' | 'backup' | 'ai' | 'certifications';
  eyebrow: string;
  title: string;
  intro: string;
  items: TrustItem[];
  /** Rendered as a table under the items (DPA section only). */
  subprocessors?: Subprocessor[];
  /** A note under the section, used where the honest answer is "ask us". */
  note?: string;
}

export interface TrustFaq {
  q: string;
  a: string;
}

export interface TrustPage {
  path: string;
  seoTitle: string;
  metaDescription: string;
  eyebrow: string;
  h1: string;
  excerpt: string;
  intro: string[];
  sections: TrustSection[];
  /** Where the DPA is requested. TODO(verify: dpa-download): swap for a download URL once one is published. */
  dpaRequestHref: string;
  securityRequestHref: string;
  faqs: TrustFaq[];
  internalLinks: Array<{ label: string; href: string }>;
}

export const TRUST_PAGE: TrustPage = {
  path: TRUST_PATH,
  seoTitle: 'Trust & Security Centre: GDPR, Data & AI | CLEAR Talent',
  metaDescription:
    'How CLEAR Talent protects employee performance data: GDPR tooling, a DPA, hosting and data residency, encryption, access control and how Ava uses your data.',
  eyebrow: 'Trust & security',
  h1: 'How CLEAR Talent protects your employee data',
  excerpt:
    'Ratings, written feedback and development notes are some of the most sensitive records an organisation keeps about its people. This page sets out how CLEAR Talent handles them — GDPR tooling, hosting, the DPA, encryption, access, sign-in and AI — and says just as plainly where the answer has to come from a conversation rather than a web page.',
  intro: [
    'A security review for performance software usually starts with the same questionnaire: where the data lives, who can see it, how it is protected in transit, how a subject access request is answered, what the AI does with it and what happens when the contract ends. The sections below answer each of those in the order your DPO and IT team are likely to ask them.',
    'CLEAR Talent holds performance and development data — goals, competency assessments, reviews, feedback and development plans — not payroll, bank details or customer data. That narrows the review, but it does not make it a formality, and nothing here replaces the documents your procurement process will ask for in writing.',
  ],
  sections: [
    {
      id: 'gdpr',
      eyebrow: 'GDPR',
      title: 'Data-rights tooling built into the platform.',
      intro:
        'Your organisation is the controller of its employees’ performance data; CLEAR Talent processes it on your behalf. These are the controls the platform provides for the parts of a GDPR programme that touch that data.',
      items: [
        {
          title: 'Subject access request exports',
          body: 'When an employee asks for a copy of their data, HR can answer the DSAR from the platform with an export, rather than searching review forms, inboxes and spreadsheets by hand.',
        },
        {
          title: 'Right-to-be-forgotten workflows',
          body: 'Erasure requests are handled through a defined workflow, so they are dealt with consistently rather than depending on who happens to pick them up.',
        },
        {
          title: 'Consent and access logged',
          body: 'Consent records, sign-ins, exports, approvals and rating changes are logged against the user responsible, with before-and-after values on every change.',
        },
        {
          title: 'Your records, exportable on every plan',
          body: 'Every plan includes export to CSV, Excel and PDF, so the performance record is never held hostage by the tool that stores it.',
        },
      ],
      note: 'Compliance also depends on how you configure and use the platform — lawful basis, retention policy and who is given which role are your decisions — so your DPO should review the DPA and the settings as part of the evaluation.',
    },
    {
      id: 'residency',
      eyebrow: 'Hosting & data residency',
      title: 'Where employee data lives.',
      intro:
        'CLEAR Talent runs on Google Cloud Platform enterprise infrastructure, whose global data centres support localised storage and regional compliance requirements.',
      // TODO(verify: hosting-region): name the default region, and state an EU
      // region only once it is confirmed and contractually committed. The
      // Ireland and Europe page test forbids "hosted in the EU" until then.
      items: [
        {
          title: 'Google Cloud Platform infrastructure',
          body: 'The platform is hosted on Google Cloud Platform, with continuous infrastructure scanning and real-time alerting through GCP Security Command Center Premium.',
        },
        {
          title: 'Regional requirements agreed per deployment',
          body: 'If your organisation needs employee data held in a particular region, such as the EU, raise it at the start of the conversation. Regional requirements are agreed with the team for each deployment.',
        },
      ],
      note: 'Get the hosting region confirmed in writing alongside the DPA rather than taking it from a web page — including this one.',
    },
    {
      id: 'dpa',
      eyebrow: 'DPA & subprocessors',
      title: 'The processor relationship, documented before any data is loaded.',
      intro:
        'A data processing agreement is available, so the terms on which CLEAR Talent processes your employees’ data are agreed before the first record goes in.',
      items: [
        {
          title: 'Data processing agreement',
          body: 'Request the DPA from the team and have your DPO or counsel review it during the evaluation, not after the contract is signed.',
        },
        {
          title: 'Subprocessors',
          body: 'The infrastructure provider that hosts the platform is listed below. Ask for the complete, current subprocessor list — with the purpose and location of each — alongside the DPA, and check it against your own register.',
        },
      ],
      // TODO(verify: subprocessors): publish the complete list once confirmed,
      // including the AI model provider (currently "details under NDA" on the AI
      // page). Do not add a name the repository cannot evidence.
      subprocessors: [
        {
          name: 'Google Cloud Platform',
          purpose: 'Application hosting, infrastructure and threat monitoring',
          location: 'Region agreed per deployment',
        },
      ],
    },
    {
      id: 'security',
      eyebrow: 'Encryption & access control',
      title: 'Protected in transit, limited by role, logged throughout.',
      intro:
        'Security is layered: encryption on every connection, permissions kept to the minimum, short-lived file links and an audit trail behind every change.',
      // TODO(verify: encryption-at-rest, pen-testing): add at-rest encryption and
      // any independent testing only once confirmed.
      items: [
        {
          title: 'TLS on all data in transit',
          body: 'All data in transit is encrypted with TLS — including traffic between internal services, caching and the API — so there are no unencrypted pathways.',
        },
        {
          title: 'Signed links that expire after 24 hours',
          body: 'Documents, profile files and exports are served through signed URLs that expire after 24 hours, so a forwarded or leaked link stops working on its own.',
        },
        {
          title: 'Role-based access with ownership checks',
          body: 'Administrators, managers and employees each work within what their role allows, and access to an individual record is checked against who owns it. Managers work within their own reporting line.',
        },
        {
          title: 'Least-privilege infrastructure',
          body: 'Every service, pipeline and storage bucket operates with only the permissions it needs, which limits what any single compromised component could reach.',
        },
        {
          title: 'Audit logging on every change',
          body: 'Logins, database operations and data changes are recorded with a timestamp and user identity, so you can show who changed a rating, when and from what.',
        },
        {
          title: 'A+ Mozilla Observatory rating',
          body: 'The platform holds an A+ rating from Mozilla Observatory, an independent check of web security headers and configuration that anyone can rerun.',
        },
      ],
    },
    {
      id: 'sign-in',
      eyebrow: 'Sign-in & SSO',
      title: 'How people sign in — and what is not available today.',
      intro:
        'Identity-provider integration is the question IT teams ask first, so here is the straight answer.',
      items: [
        {
          title: 'CLEAR Talent credentials',
          body: 'People sign in with their own CLEAR Talent credentials, and what they can see once signed in is governed by their role and the ownership checks above.',
        },
        {
          title: 'Biometric sign-in on mobile',
          body: 'The native iOS and Android apps support biometric sign-in, so employees completing reviews on a phone are not relying on a remembered password.',
        },
        {
          title: 'No SAML SSO or SCIM today',
          body: 'CLEAR Talent does not currently offer SAML single sign-on through an identity provider such as Microsoft Entra ID, Okta or Google Workspace, and there is no SCIM provisioning. If your IT policy requires SSO for every business application, raise it before you shortlist.',
        },
      ],
    },
    {
      id: 'backup',
      eyebrow: 'Backup & retention',
      title: 'Keeping the record intact, and letting it go when it should.',
      intro:
        'A performance record has to survive an incident and be deletable on request. These are the parts the platform handles today.',
      // TODO(verify: backup-retention): publish backup frequency, backup
      // retention, recovery objectives and post-contract deletion timing once
      // confirmed. Until then the page asks the buyer to get them in writing.
      items: [
        {
          title: 'Closed cycles stay closed',
          body: 'Each review stage opens and closes on the dates you set, so a rating cannot be quietly amended after the cycle ends — and the audit log records every change made while it was open.',
        },
        {
          title: 'Erasure when it is due',
          body: 'Right-to-be-forgotten workflows let HR remove an individual’s data consistently when an erasure request or your own retention policy requires it.',
        },
        {
          title: 'Your data back when you leave',
          body: 'Export to CSV, Excel and PDF is included on every plan, so you can take a complete copy of your records out before a contract ends.',
        },
      ],
      note: 'Backup frequency, how long backups are kept, recovery objectives and how soon data is deleted after a contract ends belong in your contract. Ask for them in writing during the evaluation.',
    },
    {
      id: 'ai',
      eyebrow: 'AI governance',
      title: 'What Ava does with employee data — and what she never does.',
      intro:
        'Ava, the AI assistant built into CLEAR Talent, drafts review narratives from the evidence already in the platform and summarises 360° feedback. The manager writes and owns the review. The EU AI Act treats AI used to evaluate workers’ performance as high-risk, and Ava was designed around the oversight, transparency and record-keeping that implies.',
      // TODO(verify: ai-training): the strongest statement the repository
      // supports is "never used to train models for other customers". Do not
      // widen it to "never used to train any model" until confirmed.
      items: [
        {
          title: 'Works from your evidence',
          body: 'Ava reads the goal scores, competency ratings, 360° feedback and check-in notes already recorded for an employee in the platform and turns them into a first-draft review narrative for the manager to work from.',
        },
        {
          title: 'Never trains a shared model',
          body: 'Ava runs on isolated tenants, and your employees’ data is never used to train models for other customers.',
        },
        {
          title: 'A human decides, every time',
          body: 'Every Ava output is labelled as AI-generated, editable and approved by a person. Ava never makes the final rating; managers accept, amend or reject her drafts, and every edit is logged.',
        },
        {
          title: 'Switch it off where you need to',
          body: 'AI can be turned off per cycle, per team or per module — useful when a works council, a union agreement or your own policy calls for a process without it.',
        },
      ],
      note: 'CLEAR Talent does not certify your compliance with the EU AI Act. The team will work through the specifics of your deployment with your compliance function.',
    },
    {
      id: 'certifications',
      eyebrow: 'Certifications & audits',
      title: 'Ask for the evidence your procurement process requires.',
      intro:
        'Security questionnaires often ask for a specific certification or third-party audit report. Ask for it by name at the start of the evaluation and you will get a plain answer about what can be provided.',
      // TODO(verify: certifications): name SOC 2, ISO/IEC 27001 or any other
      // certification here only once the company confirms it holds it.
      items: [
        {
          title: 'Security questionnaires',
          body: 'Send your standard questionnaire through the contact form and the team will work through it with you, alongside the DPA and the subprocessor list.',
        },
      ],
    },
  ],
  dpaRequestHref: '/contact?reason=dpa&source=trust',
  securityRequestHref: '/contact?reason=security&source=trust',
  faqs: [
    {
      q: 'Is CLEAR Talent GDPR compliant?',
      a: 'CLEAR Talent provides the controls a GDPR programme needs for performance data: subject access request exports, right-to-be-forgotten workflows, consent and access logging, role-based access and a data processing agreement. Compliance also depends on how you configure and use the platform, so your DPO should review the DPA and settings during the evaluation.',
    },
    {
      q: 'Where is our employee data hosted, and can it stay in the EU?',
      a: 'CLEAR Talent is hosted on Google Cloud Platform infrastructure, monitored continuously by GCP Security Command Center Premium. If you need data held in a particular region, such as the EU, raise it at the start of the conversation: regional requirements are agreed for each deployment and should be confirmed in writing alongside the DPA.',
    },
    {
      q: 'Can we see the DPA and the subprocessor list?',
      a: 'Yes. A data processing agreement is available on request, and you should ask for the complete, current subprocessor list at the same time so your DPO can review both before any employee data is loaded.',
    },
    {
      q: 'Is our employee data used to train AI models?',
      a: 'Your employees’ data is never used to train models for other customers. Ava runs on isolated tenants, every AI output is labelled, editable and approved by a person, and AI can be switched off per cycle, team or module.',
    },
    {
      q: 'Do you support single sign-on (SAML) or SCIM?',
      a: 'Not currently. People sign in with their own CLEAR Talent credentials, with biometric sign-in in the iOS and Android apps, and access is role-based with ownership checks and audit logging. If SSO is mandatory under your IT policy, tell us at the start of the evaluation.',
    },
    {
      q: 'Can we take our data with us if we leave?',
      a: 'Yes. Every plan includes export, and records and reports come out as CSV, Excel or PDF. Ask for post-contract deletion timing to be set out in your contract.',
    },
  ],
  internalLinks: [
    { label: 'Performance management for Ireland and Europe', href: '/solutions/performance-management-software-europe' },
    { label: 'How Ava keeps managers in charge', href: '/solutions/ai-performance-management' },
    { label: 'Integrations, imports and DSAR exports', href: '/integrations' },
    { label: 'Plans and what each includes', href: '/pricing' },
  ],
};

export const getTrustBreadcrumbs = (page: TrustPage = TRUST_PAGE): BreadcrumbItem[] => [
  { name: 'Home', path: '/' },
  { name: 'Trust & Security', path: page.path },
];

/**
 * The route's nodes: a WebPage about the organisation and published by it,
 * FAQPage and BreadcrumbList — all from the copy above. `siteSeo.ts` ships the
 * shared Organization, WebSite and SoftwareApplication nodes they reference.
 */
export const getSchemaForTrust = (page: TrustPage = TRUST_PAGE): JsonLd[] => {
  const url = `${SITE_ORIGIN}${page.path}`;

  return [
    {
      '@context': SCHEMA_CONTEXT,
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: page.seoTitle,
      description: page.metaDescription,
      isPartOf: { '@id': WEBSITE_ID },
      about: { '@id': ORG_ID },
      mentions: { '@id': SOFTWARE_ID },
      publisher: { '@id': ORG_ID },
      dateModified: TRUST_LAST_MODIFIED,
      inLanguage: 'en',
    },
    {
      '@context': SCHEMA_CONTEXT,
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      mainEntity: page.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: { '@type': 'Answer', text: faq.a },
      })),
    },
    breadcrumbListSchema(getTrustBreadcrumbs(page)),
  ];
};
